/**
 * @module agent/adapters/adapterStorageUtils
 *
 * Shared storage utilities for agent adapters (AgentCoreAdapter, SimpleMockAdapter).
 * Implements TD-1845 security fixes:
 * - User-scoped storage keys to prevent cross-user data exposure
 * - Logout cleanup to clear sensitive M&A data
 * - Defense-in-depth fallback for orphaned keys
 *
 * @see AgentCoreAdapter.js
 * @see SimpleMockAdapter.js
 */

/**
 * TD-1845: User-scoped storage key prefixes to prevent cross-user data exposure
 * Keys are appended with user ID: `{prefix}_{userId}`
 */
export const STORAGE_KEY_PREFIX = {
  SESSIONS: 'agent_sessions_v2',
  MESSAGES: 'agent_messages_v2'
};

/**
 * Get user-scoped storage key for sessions or messages.
 * TD-1845 Fix #4: Throws instead of returning null to fail fast with specific error
 *
 * @example
 * const key = getStorageKey('SESSIONS', 'user-123');
 * // Returns: 'agent_sessions_v2_user-123'
 *
 * @param {string} type - 'SESSIONS' or 'MESSAGES'
 * @param {string} userId - Current user's ID
 * @param {string} [adapterName='ADAPTER'] - Adapter name for error messages
 * @returns {string} User-scoped storage key
 * @throws {Error} If userId is not provided
 */
export const getStorageKey = (type, userId, adapterName = 'ADAPTER') => {
  if (!userId) {
    throw new Error(
      `🔒 ${adapterName}: Cannot generate storage key without user ID (type: ${type})`
    );
  }
  return `${STORAGE_KEY_PREFIX[type]}_${userId}`;
};

/**
 * Get current user ID from store's authStore.
 * TD-1845 Fix #8: Uses currentUserId as single source of truth.
 *
 * [Review Fix #4]: Explicitly differentiates null/undefined/empty string states
 * to help diagnose authentication bugs where empty string might be incorrectly set.
 *
 * @example
 * const userId = getCurrentUserId(store, 'AGENTCORE');
 * if (!userId) console.warn('No authenticated user');
 *
 * @param {Object} store - Store containing authStore
 * @param {string} [adapterName='ADAPTER'] - Adapter name for log messages
 * @returns {string|null} Current user's ID or null if not authenticated
 */
export const getCurrentUserId = (store, adapterName = 'ADAPTER') => {
  // AuthStore.currentUserId is a getter that returns this.userInfo?.id
  const userId = store?.authStore?.currentUserId;

  // [Review Fix #4]: Explicit type checking to catch state bugs
  if (userId === undefined || userId === null) {
    if (import.meta.env.DEV) console.warn(`🔒 ${adapterName}: No authenticated user (currentUserId is ${userId})`);
    return null;
  }
  if (userId === '') {
    console.error(`🔒 ${adapterName}: INVALID STATE - currentUserId is empty string`);
    return null;
  }
  return userId;
};

/**
 * Clear user-scoped localStorage keys for sessions and messages.
 *
 * @example
 * clearUserStorage('user-123', 'SIMPLE MOCK');
 *
 * @param {string} userId - User ID to clear storage for
 * @param {string} [adapterName='ADAPTER'] - Adapter name for log messages
 */
export const clearUserStorage = (userId, adapterName = 'ADAPTER') => {
  try {
    const sessionsKey = getStorageKey('SESSIONS', userId, adapterName);
    const messagesKey = getStorageKey('MESSAGES', userId, adapterName);

    localStorage.removeItem(sessionsKey);
    localStorage.removeItem(messagesKey);

    if (import.meta.env.DEV) console.log(`🔒 ${adapterName}: Cleared localStorage for user:`, userId);
  } catch (error) {
    console.error(`🔒 ${adapterName}: Error clearing user localStorage:`, error);
  }
};

/**
 * TD-1845 Fix #3: Defense-in-depth - clear ALL agent localStorage keys.
 * Used when userId is unavailable during logout to prevent data exposure.
 *
 * [Review Fix #2]: Sanitizes localStorage keys before logging to prevent
 * potential injection via maliciously crafted key names.
 *
 * @example
 * clearAllAgentStorage('AGENTCORE');
 *
 * @param {string} [adapterName='ADAPTER'] - Adapter name for log messages
 */
export const clearAllAgentStorage = (adapterName = 'ADAPTER') => {
  if (import.meta.env.DEV) console.warn(
    `🔒 ${adapterName}: No user ID available - clearing ALL agent localStorage as defense-in-depth`
  );
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('agent_sessions_v2_') || key.startsWith('agent_messages_v2_')) {
        localStorage.removeItem(key);
        // [Review Fix #2]: Sanitize key before logging (truncate to safe length)
        const sanitizedKey = key.substring(0, 100);
        if (import.meta.env.DEV) console.log(`🔒 ${adapterName}: Removed orphaned key:`, sanitizedKey);
      }
    });
  } catch (error) {
    console.error(`🔒 ${adapterName}: Error clearing all agent localStorage:`, error);
  }
};

/**
 * Load sessions from localStorage for a specific user.
 * Returns empty Map if no user ID or stored data.
 *
 * [Review Fix #6]: If a session lacks `createdAt`, the current date is used as fallback.
 * Callers needing migration tracking (like AgentCoreAdapter) should use the
 * sessionTransformer to detect and handle migration scenarios.
 *
 * @example
 * const sessions = loadStoredSessions(store, 'SIMPLE MOCK');
 *
 * // With transformer for migration detection:
 * const sessions = loadStoredSessions(store, 'AGENTCORE', (session, id) => {
 *   if (!session.lastActivity) {
 *     migrationNeeded = true;
 *     session.lastActivity = session.createdAt;
 *   }
 *   return session;
 * });
 *
 * @param {Object} store - Store containing authStore
 * @param {string} [adapterName='ADAPTER'] - Adapter name for log messages
 * @param {Function} [sessionTransformer] - Optional function(session, sessionId) to transform each session
 * @returns {Map<string, Object>} Map of session ID to session object
 */
export const loadStoredSessions = (store, adapterName = 'ADAPTER', sessionTransformer = null) => {
  try {
    const userId = getCurrentUserId(store, adapterName);
    if (!userId) {
      if (import.meta.env.DEV) console.warn(`🔒 ${adapterName}: No user ID - skipping session load`);
      return new Map();
    }

    const storageKey = getStorageKey('SESSIONS', userId, adapterName);
    const stored = localStorage.getItem(storageKey);
    if (!stored) return new Map();

    // [Review Fix #1]: Wrap JSON.parse in nested try-catch for security
    let parsed;
    try {
      parsed = JSON.parse(stored);
    } catch (parseError) {
      if (import.meta.env.DEV) console.warn(`⚠️ ${adapterName}: Invalid JSON in sessions storage, clearing corrupted data`);
      localStorage.removeItem(storageKey);
      return new Map();
    }

    const sessions = new Map();

    Object.entries(parsed).forEach(([id, session]) => {
      try {
        const baseSession = {
          ...session,
          createdAt: session.createdAt ? new Date(session.createdAt) : new Date()
        };

        // Apply optional transformer for adapter-specific processing
        const finalSession = sessionTransformer ? sessionTransformer(baseSession, id) : baseSession;
        sessions.set(id, finalSession);
      } catch (transformError) {
        if (import.meta.env.DEV) console.warn(`⚠️ ${adapterName}: Failed to transform session ${id}, skipping`);
      }
    });

    return sessions;
  } catch (error) {
    if (import.meta.env.DEV) console.warn(`⚠️ ${adapterName}: Failed to load sessions:`, error);
    return new Map();
  }
};

/**
 * Load messages from localStorage for a specific user.
 * Returns empty Map if no user ID or stored data.
 *
 * [Review Fix #5]: Transformer receives (message, sessionId) for consistency
 * with sessionTransformer signature, allowing message transformations that
 * need session context.
 *
 * @example
 * const messages = loadStoredMessages(store, 'AGENTCORE', (msg, sessionId) => ({
 *   ...msg,
 *   sessionContext: sessionId
 * }));
 *
 * @param {Object} store - Store containing authStore
 * @param {string} [adapterName='ADAPTER'] - Adapter name for log messages
 * @param {Function} [messageTransformer] - Optional function(message, sessionId) to transform each message
 * @returns {Map<string, Array>} Map of session ID to message array
 */
export const loadStoredMessages = (store, adapterName = 'ADAPTER', messageTransformer = null) => {
  try {
    const userId = getCurrentUserId(store, adapterName);
    if (!userId) {
      if (import.meta.env.DEV) console.warn(`🔒 ${adapterName}: No user ID - skipping messages load`);
      return new Map();
    }

    const storageKey = getStorageKey('MESSAGES', userId, adapterName);
    const stored = localStorage.getItem(storageKey);
    if (!stored) return new Map();

    // [Review Fix #1]: Wrap JSON.parse in nested try-catch for security
    let parsed;
    try {
      parsed = JSON.parse(stored);
    } catch (parseError) {
      if (import.meta.env.DEV) console.warn(`⚠️ ${adapterName}: Invalid JSON in messages storage, clearing corrupted data`);
      localStorage.removeItem(storageKey);
      return new Map();
    }

    const messages = new Map();

    Object.entries(parsed).forEach(([sessionId, sessionMessages]) => {
      try {
        messages.set(
          sessionId,
          sessionMessages.map((msg) => {
            const baseMsg = {
              ...msg,
              timestamp: new Date(msg.timestamp)
            };
            // [Review Fix #5]: Pass sessionId to transformer for consistency
            return messageTransformer ? messageTransformer(baseMsg, sessionId) : baseMsg;
          })
        );
      } catch (transformError) {
        if (import.meta.env.DEV) console.warn(`⚠️ ${adapterName}: Failed to transform messages for session ${sessionId}`);
      }
    });

    return messages;
  } catch (error) {
    if (import.meta.env.DEV) console.warn(`⚠️ ${adapterName}: Failed to load messages:`, error);
    return new Map();
  }
};

/**
 * Save sessions to localStorage for a specific user.
 * TD-1845 Fix #6: Validates userId before save to prevent race condition during logout.
 *
 * [Review Fix #3]: Handles QuotaExceededError specifically to provide clear feedback
 * when localStorage is full.
 *
 * @example
 * saveStoredSessions(store, sessions, 'SIMPLE MOCK');
 *
 * @param {Object} store - Store containing authStore
 * @param {Map<string, Object>} sessions - Map of session ID to session object
 * @param {string} [adapterName='ADAPTER'] - Adapter name for log messages
 */
export const saveStoredSessions = (store, sessions, adapterName = 'ADAPTER') => {
  try {
    const userId = getCurrentUserId(store, adapterName);
    if (!userId) {
      if (import.meta.env.DEV) console.warn(
        `🔒 ${adapterName}: No user ID - skipping session save (logout may be in progress)`
      );
      return;
    }

    const storageKey = getStorageKey('SESSIONS', userId, adapterName);
    const sessionsObject = {};
    sessions.forEach((session, id) => {
      sessionsObject[id] = session;
    });
    localStorage.setItem(storageKey, JSON.stringify(sessionsObject));
  } catch (error) {
    // [Review Fix #3]: Handle quota exceeded specifically
    if (error.name === 'QuotaExceededError') {
      console.error(`🔒 ${adapterName}: localStorage quota exceeded - unable to save sessions`);
    } else if (import.meta.env.DEV) {
      console.warn(`⚠️ ${adapterName}: Failed to save sessions:`, error);
    }
  }
};

/**
 * Save messages to localStorage for a specific user.
 * TD-1845: User-scoped storage prevents cross-user data exposure.
 *
 * [Review Fix #3]: Handles QuotaExceededError specifically to provide clear feedback
 * when localStorage is full.
 *
 * @example
 * saveStoredMessages(store, messages, 'AGENTCORE');
 *
 * @param {Object} store - Store containing authStore
 * @param {Map<string, Array>} messages - Map of session ID to message array
 * @param {string} [adapterName='ADAPTER'] - Adapter name for log messages
 */
export const saveStoredMessages = (store, messages, adapterName = 'ADAPTER') => {
  try {
    const userId = getCurrentUserId(store, adapterName);
    if (!userId) {
      if (import.meta.env.DEV) console.warn(`🔒 ${adapterName}: No user ID - skipping messages save`);
      return;
    }

    const storageKey = getStorageKey('MESSAGES', userId, adapterName);
    const messagesObject = {};
    messages.forEach((sessionMessages, sessionId) => {
      messagesObject[sessionId] = sessionMessages;
    });
    localStorage.setItem(storageKey, JSON.stringify(messagesObject));
  } catch (error) {
    // [Review Fix #3]: Handle quota exceeded specifically
    if (error.name === 'QuotaExceededError') {
      console.error(`🔒 ${adapterName}: localStorage quota exceeded - unable to save messages`);
    } else if (import.meta.env.DEV) {
      console.warn(`⚠️ ${adapterName}: Failed to save messages:`, error);
    }
  }
};

/**
 * Create logout handler for adapter cleanup.
 * TD-1845: Critical security fix to prevent data exposure after logout.
 *
 * [Review Fix #8, #13]: This handler performs 3 cleanup phases in order:
 * 1. **userId refresh**: Attempts to get fresh userId from store in case of re-login scenario
 * 2. **Storage clear**: Removes user-scoped localStorage keys (or all agent keys if no userId)
 * 3. **Memory clear**: Clears in-memory sessions and messages Maps
 *
 * NOTE: Uses `cachedUserIdRef` object pattern (with `value` property) to allow mutation
 * across the closure boundary. This is necessary because the handler is created once
 * at adapter construction but needs to read/write the cached userId at logout time.
 * The pattern avoids recreating the handler on every userId change.
 *
 * @example
 * // In adapter constructor:
 * this._cachedUserIdRef = { value: getCurrentUserId(store, ADAPTER_NAME) };
 * const logoutCleanup = createLogoutHandler(
 *   store, this.sessions, this.messages, this._cachedUserIdRef, ADAPTER_NAME
 * );
 * this._logoutHandler = (payload) => {
 *   if (payload?.event === 'logout') logoutCleanup();
 * };
 * pubSub.subscribe(this._logoutHandler);
 *
 * @param {Object} store - Store containing authStore
 * @param {Map} sessions - Sessions Map to clear
 * @param {Map} messages - Messages Map to clear
 * @param {Object} cachedUserIdRef - Object with `value` property holding cached user ID.
 *   This object pattern allows the handler to read and update the cached userId across
 *   the closure boundary without recreating the handler.
 * @param {string} [adapterName='ADAPTER'] - Adapter name for log messages
 * @returns {Function} Cleanup function to call on logout
 */
export const createLogoutHandler = (
  store,
  sessions,
  messages,
  cachedUserIdRef,
  adapterName = 'ADAPTER'
) => {
  return () => {
    // Phase 1: Refresh userId from store if available (handles re-login scenarios)
    // TD-1845 Fix #1/#2: User may have re-logged in as different user without page reload
    const freshUserId = getCurrentUserId(store, adapterName);
    if (freshUserId) {
      cachedUserIdRef.value = freshUserId;
    }

    const userId = cachedUserIdRef.value;
    if (import.meta.env.DEV) console.log(`🔒 ${adapterName}: Handling logout, clearing user data for:`, userId);

    // Phase 2: Clear user-scoped localStorage keys
    if (userId) {
      clearUserStorage(userId, adapterName);
    } else {
      // TD-1845 Fix #3: Defense-in-depth fallback when userId unavailable
      clearAllAgentStorage(adapterName);
    }

    // Phase 3: Clear in-memory data
    sessions.clear();
    messages.clear();

    // Reset cached userId after cleanup
    cachedUserIdRef.value = null;

    if (import.meta.env.DEV) console.log(`🔒 ${adapterName}: Cleared in-memory sessions and messages`);
  };
};

/**
 * ✅ [TD-1703] PREVENTION: Cache validation layer
 * Validates localStorage cache integrity and logs warnings for inconsistencies.
 * Detects orphaned messages, invalid session data, and cache corruption.
 *
 * @example
 * const report = validateCache(sessions, messages, { autoCleanup: true }, 'AGENTCORE');
 * if (!report.isValid) console.error('Cache has errors:', report.errors);
 *
 * @param {Map<string, Object>} sessions - Sessions Map to validate
 * @param {Map<string, Array>} messages - Messages Map to validate
 * @param {Object} options - Validation options
 * @param {boolean} options.autoCleanup - Automatically clean up orphaned data (default: false)
 * @param {Function} [options.onSaveMessages] - Callback to persist message changes after cleanup
 * @param {string} [adapterName='ADAPTER'] - Adapter name for log messages
 * @returns {Object} Validation report with warnings and errors
 */
export const validateCache = (sessions, messages, options = {}, adapterName = 'ADAPTER') => {
  const { autoCleanup = false, onSaveMessages = null } = options;
  const warnings = [];
  const errors = [];

  if (import.meta.env.DEV) console.log(`🔍 ${adapterName}: Starting cache validation...`);

  // Check 1: Orphaned messages (messages without corresponding sessions)
  const orphanedMessageSessionIds = [];
  for (const [sessionId] of messages) {
    if (!sessions.has(sessionId)) {
      orphanedMessageSessionIds.push(sessionId);
      warnings.push({
        type: 'orphaned_messages',
        sessionId,
        message: `Found messages for non-existent session: ${sessionId}`
      });
    }
  }

  if (orphanedMessageSessionIds.length > 0) {
    if (import.meta.env.DEV) console.warn(
      `⚠️ ${adapterName}: Found`,
      orphanedMessageSessionIds.length,
      'orphaned message collections:',
      orphanedMessageSessionIds
    );

    if (autoCleanup) {
      orphanedMessageSessionIds.forEach((sessionId) => {
        messages.delete(sessionId);
        if (import.meta.env.DEV) console.log(`🧹 ${adapterName}: Auto-cleaned orphaned messages for session:`, sessionId);
      });
      if (onSaveMessages) {
        onSaveMessages();
      }
    }
  }

  // Check 2: Invalid session data (missing required fields)
  const invalidSessions = [];
  for (const [sessionId, session] of sessions) {
    const missingFields = [];

    if (!session.id) missingFields.push('id');
    if (!session.runtimeSessionId) missingFields.push('runtimeSessionId');
    if (!session.createdAt) missingFields.push('createdAt');
    if (!session.lastActivity) missingFields.push('lastActivity');

    // Check for ID mismatch (both should be equal after TD-1703 fix)
    if (session.id !== session.runtimeSessionId) {
      errors.push({
        type: 'id_mismatch',
        sessionId,
        message: `Session ID mismatch: id=${session.id}, runtimeSessionId=${session.runtimeSessionId}`,
        data: { id: session.id, runtimeSessionId: session.runtimeSessionId }
      });
    }

    if (missingFields.length > 0) {
      invalidSessions.push({ sessionId, missingFields });
      warnings.push({
        type: 'invalid_session',
        sessionId,
        message: `Session missing required fields: ${missingFields.join(', ')}`,
        data: { missingFields }
      });
    }
  }

  if (invalidSessions.length > 0) {
    if (import.meta.env.DEV) console.warn(
      `⚠️ ${adapterName}: Found`,
      invalidSessions.length,
      'invalid sessions:',
      invalidSessions
    );
  }

  // Check 3: Session-message count consistency
  const sessionCount = sessions.size;
  const messageCollectionCount = messages.size;
  if (sessionCount !== messageCollectionCount && orphanedMessageSessionIds.length === 0) {
    warnings.push({
      type: 'count_mismatch',
      message: `Session/message count mismatch: ${sessionCount} sessions, ${messageCollectionCount} message collections`,
      data: { sessionCount, messageCollectionCount }
    });
    if (import.meta.env.DEV) console.warn(
      `⚠️ ${adapterName}: Session/message count mismatch:`,
      sessionCount,
      'sessions,',
      messageCollectionCount,
      'message collections'
    );
  }

  // Generate report
  const report = {
    timestamp: new Date().toISOString(),
    sessionCount,
    messageCollectionCount,
    warnings,
    errors,
    isValid: errors.length === 0,
    hasWarnings: warnings.length > 0,
    summary: {
      orphanedMessages: orphanedMessageSessionIds.length,
      invalidSessions: invalidSessions.length,
      idMismatches: errors.filter((e) => e.type === 'id_mismatch').length
    }
  };

  if (import.meta.env.DEV) {
    if (report.isValid && !report.hasWarnings) {
      console.log(`✅ ${adapterName}: Cache validation passed - no issues found`);
    } else {
      console.warn(`⚠️ ${adapterName}: Cache validation completed with issues:`, report.summary);
    }
  }

  return report;
};
